import { AgGridReact, DropdownButton, Flex, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Sizes } from "@jsq/ds/styles";
import { getFullLegalName } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/utils";
import { TABLE_GRID_OPTIONS } from "../../constants";
import * as styles from "./styles_vanilla.css";
import type { ControllingPersonEntry } from "../../types";

export type GridContext = {
  onEdit: (entry: ControllingPersonEntry) => void;
  onDelete: (entryId: string) => void;
  getCountryName: (code: string) => string;
  readonly: boolean;
};

const formatAddress = (
  entry: ControllingPersonEntry,
  getCountryName: (code: string) => string,
): string => {
  const { streetLine1, streetLine2, city, stateOrProvince, zipCode, country } = entry.address;
  const cityStateZip = [city, stateOrProvince, zipCode].filter(Boolean).join(", ");
  const countryName = country ? getCountryName(country) : "";
  return [streetLine1, streetLine2, cityStateZip, countryName].filter(Boolean).join("\n");
};

const ActionsCellRenderer = (
  params: AgGridReact.CustomCellRendererProps<ControllingPersonEntry, unknown, GridContext>,
) => {
  const { data, context } = params;
  if (!data || !context) return null;

  return (
    <DropdownButton
      data-testid={`controlling-person-${data.id}-actions`}
      variant="muted"
      icon="ellipsis-h"
      direction="down"
      align="right"
      usePortal
    >
      <DropdownButton.Action disabled={context.readonly} onSelect={() => context.onEdit(data)}>
        <Flex alignItems="center" gap={Sizes.SM}>
          <Icon type="edit" />
          <Text>Edit</Text>
        </Flex>
      </DropdownButton.Action>
      <DropdownButton.Action
        disabled={context.readonly}
        onSelect={() => context.onDelete(data.id)}
      >
        <Flex alignItems="center" gap={Sizes.SM}>
          <Icon type="trash" />
          <Text>Delete</Text>
        </Flex>
      </DropdownButton.Action>
    </DropdownButton>
  );
};

export const columnDefs: AgGridReact.AgGridReactProps<ControllingPersonEntry>["columnDefs"] = [
  {
    headerName: "Name",
    flex: 1,
    sortable: false,
    cellClass: styles.gridCell,
    headerClass: styles.gridHeader,
    valueGetter: (params) =>
      params.data
        ? getFullLegalName(params.data.firstName, params.data.lastName, params.data.middleName)
        : "",
    cellRenderer: (
      params: AgGridReact.CustomCellRendererProps<ControllingPersonEntry, string>,
    ) => <Text className={styles.cellContent}>{params.value}</Text>,
  },
  {
    headerName: "Residence address",
    flex: 2,
    sortable: false,
    cellClass: styles.gridCell,
    headerClass: styles.gridHeader,
    cellRenderer: (
      params: AgGridReact.CustomCellRendererProps<ControllingPersonEntry, string, GridContext>,
    ) => {
      if (!params.data || !params.context) return null;
      const address = formatAddress(params.data, params.context.getCountryName);
      return <Text className={styles.preLineCellContent}>{address}</Text>;
    },
  },
  {
    headerName: "TIN",
    flex: 1,
    sortable: false,
    cellClass: styles.gridCell,
    headerClass: styles.gridHeader,
    valueGetter: (params) =>
      params.data?.tinNotRequired ? "Not legally required" : (params.data?.tin ?? ""),
    cellRenderer: (
      params: AgGridReact.CustomCellRendererProps<ControllingPersonEntry, string>,
    ) =>
      params.value ? (
        <Text className={styles.cellContent}>{params.value}</Text>
      ) : (
        <Text className={styles.naText} variant="secondary-md">
          N/A
        </Text>
      ),
  },
  {
    colId: "actions",
    headerName: "",
    width: 56,
    sortable: false,
    suppressHeaderMenuButton: true,
    suppressMovable: true,
    cellClass: styles.actionsGridCell,
    cellRenderer: ActionsCellRenderer,
  },
];

export const gridOptions = TABLE_GRID_OPTIONS;
